<script setup lang="ts">
import { useAttrs, type SetupContext } from 'vue';
import type { Schema } from '@/api/types';
import type {
  BlockToolData,
  OutputBlockData as BaseOutputBlockData,
  OutputData,
} from '@editorjs/editorjs';

interface OutputBlockData<
  T extends string = string,
  D extends object = Record<string, unknown>,
> extends BaseOutputBlockData<T, D> {
  isPartOfAToggle?: boolean;
}

defineProps<{
  schema: Schema;
  value: OutputData;
}>();

const attrs: SetupContext['attrs'] = useAttrs();

const alertParser = (data: BlockToolData): string => {
  //For now: info, success, warning, danger.
  const colorMap: { [type: string]: string } = {
    success: 'lime',
    warning: 'amber',
    danger: 'red',
    info: 'cyan',
  };
  const color = colorMap[data.type] || '';
  return `<div
      class="text-${data.align} border-${color}-400 bg-${color}-100 text-${color}-700 relative rounded border px-4 py-3 my-8"
      role="alert"
    >
      <p class="block sm:inline">${data.message}</p>
    </div>`;
};

const attachParser = (data: BlockToolData): string => {
  const filename = data.file.name;
  const extensionFromFilename =
    filename === undefined ? '' : data.file.name.split('.').pop();
  const downloadIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M7 10L11.8586 14.8586C11.9367 14.9367 12.0633 14.9367 12.1414 14.8586L17 10"></path></svg>';
  return `<div class='attachment'>
            <div class='attachment__icon' data-extension=${extensionFromFilename}>${extensionFromFilename.toUpperCase()}</div>
            <div class='attachment__info'>
              <div class='attachment__file-title'>${data.title}</div>
              <div class='attachment__file-size'>${(Number(data.file.size) / 1024).toFixed(1)} KiB</div>
            </div>
            <a href='${data.file.url}' class='attachment__download'>${downloadIcon}</a>
          </div>`;
};

const embedParser = (data: BlockToolData): string => {
  const captionHTML = data.caption
    ? `<span class="embedCaption"> ${data.caption}</span>`
    : '';
  return `<div class="embed">
            <iframe allowfullscreen scrolling="no"
              name="${data.service}"
              src="${data.embed}"
              width="${data.width}"
              height="${data.height}"
            ></iframe>            
            ${captionHTML}
          </div>`;
};

const imgParser = (data: BlockToolData) => {
  const withBorder = data.withBorder ? 'WidgetImage--withBorder' : '';
  const stretchedClass = data.stretched ? 'w-full' : '';

  return `<div class='WidgetImage'>
    <img
      class="${stretchedClass} ${withBorder}"
      src="${data.file.url}"
      alt="${data.caption}"
    /><span class="imgCaption">${data.caption}</span>
  </div>`;
};

const linkParser = (data: BlockToolData) => {
  console.log(JSON.stringify(data));
  const descriptionHTML = data.meta.description
    ? `<p class="text-black">${data.meta.description}</p>`
    : '';
  const imageHTML = data.meta.image.url
    ? `<img src="${data.meta.image.url}" alt="Link logo" class="rounded-xs size-[85px] border" />`
    : '';
  return `<a href="${data.link}" target="_blank" class="flex flex-row justify-between gap-2 rounded-md border border-gray-300 bg-white mb-4 p-6 !no-underline hover:shadow-sm">
    <div>
      <p class="font-bold text-black">${data.meta.title}</p>
      ${descriptionHTML}
      <p class="text-black opacity-50">${data.link}</p>
    </div>
      ${imageHTML}
    </a>`;
};

const listParser = (data: BlockToolData): string => {
  const listType =
    data.style === 'unordered' || data.style === 'checklist' ? 'ul' : 'ol';
  const isChecklist = data.style === 'checklist';
  const getListItems = (items: [], listType: string, level = 0) => {
    const listItem = items.map((item: BlockToolData) => {
      const isChecked = item.meta.checked ? 'checked' : '';
      const checklistHTML = isChecklist
        ? `<input class='gnist-checkbox' type="checkbox" ${isChecked}>`
        : '';
      if (!item.content && !item.items)
        return `<li>${checklistHTML} ${item}</li>`;
      let nestedList = '';
      if (item.items?.length)
        nestedList = getListItems(item.items, listType, level + 1);
      if (item.content)
        return `<li> ${checklistHTML} ${item.content}${nestedList}</li>`;
    });
    const start = level === 0 ? `start='${data.meta.start}'` : 'start="1"';
    return `<${listType} ${start} class='list-outside' >${listItem.join('')}</${listType}>`;
  };
  return getListItems(data.items, listType);
};

const tableParser = (data: BlockToolData) => {
  const content = data.content;
  let html = '<table>\n';
  for (const row of content) {
    html += '<tr>\n';
    for (const cell of row) {
      if (data.withHeadings && row === content[0]) html += `<th>${cell}</th>\n`;
      else html += `<td>${cell}</td>\n`;
    }
    html += '</tr>\n';
  }
  html += '</table>';
  return html;
};

const toggleParser = (
  blocks: OutputBlockData[],
  block: BlockToolData,
): string => {
  const toggleIndex = blocks.indexOf(block);
  if (toggleIndex === -1) {
    console.error('Error: Block not found in blocks array');
    return '';
  }

  const numItems = block.data.items;
  const toggleElements = blocks.slice(
    toggleIndex + 1,
    toggleIndex + 1 + numItems,
  );

  const detailsContent = toggleElements.map(generateHtmlForTools).join('');
  toggleElements.forEach((element) => {
    element.isPartOfAToggle = true;
  });
  return `
  <details closed id="accordion">
    <summary id="accordion">${block.data.text}</summary>
    <div class="ml-6">${detailsContent}</div>
  </details>
  <div class="divider m-2"></div>
  `;
};

/* Parse the output data from EditorJS to HTML for each block type */
const generateHtmlForTools = (block: BlockToolData) => {
  const align = block.data.alignment || block.tunes?.alignmentTune.alignment;
  const textAlign = align ? `text-${align}` : '';

  switch (block.type) {
    case 'alert':
      return alertParser(block.data);
    case 'attaches':
      return attachParser(block.data);
    case 'embed':
      return embedParser(block.data);
    case 'header':
      return `<h${block.data.level} id='${block.id}' class='${textAlign} text-h${block.data.level}'>${block.data.text}</h${block.data.level}>`;
    case 'image':
      return imgParser(block.data);
    case 'linkTool':
      return linkParser(block.data);
    case 'list':
      return listParser(block.data);
    case 'paragraph':
      return `<p class='${textAlign}'>${block.data.text}</p>`;
    case 'quote':
      return `<blockquote class='${textAlign}'>${block.data.text} <span class='font-bold block'>${block.data.caption}</span></blockquote>`;
    case 'table':
      return tableParser(block.data);
    default:
      console.warn(`Block type unknown: '${block.type}'`);
      return '';
  }
};

function parseDataToHTML(blocks: OutputBlockData[]) {
  return blocks
    .map((block) => {
      if (block.isPartOfAToggle) return '';
      if (block.type === 'toggle') return toggleParser(blocks, block);
      return generateHtmlForTools(block);
    })
    .join('');
}
</script>

<template>
  <div v-if="value">
    <h2
      v-if="schema.showTitle"
      :id="schema.title.replace(/[^a-z1-9-_]/gi, '-')"
    >
      {{ schema.title }}
    </h2>

    <!-- EditorJS json to html -->
    <div
      v-bind="attrs"
      class="html-render"
      v-html="parseDataToHTML(value.blocks)"
    />
  </div>
</template>
